import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { CountryCode } from 'src/app/models/country-code';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { ThemeService } from 'src/app/services/client/theme/theme.service';
import { PushNotificationService } from 'src/app/services/push-notification/push-notification.service';
import { AccountService } from 'src/app/services/user/account.service';

import { UiService } from 'src/app/services/ui.service';
import { finalize } from 'rxjs';
import { GenericModalComponent } from '../modals/generic-modal/generic-modal.component';
import { TermsConditionsModalComponent } from '../modals/terms-conditions-modal/terms-conditions-modal.component';
import { PrivacyPolicyModalComponent } from '../modals/privacy-policy-modal/privacy-policy-modal.component';
import { CountryCodeModalComponent } from '../modals/country-code-modal/country-code-modal.component';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { localStorageKeys } from 'src/app/services/localStorage/localStorageVariables';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { Capacitor } from '@capacitor/core';
import { I18ConfigService } from 'src/app/services/shared/I18-config.service';

@Component({
  selector: 'app-mobile-login-component',
  templateUrl: './mobile-login-component.component.html',
  styleUrls: ['./mobile-login-component.component.scss'],
})
export class MobileLoginComponentComponent implements OnInit {
  formGrp: UntypedFormGroup;
  formGrp2: UntypedFormGroup;
  appName: string;
  appLogo: string;
  msgId: string;
  otpId: number;
  showOTPInput: boolean;
  showOTPError: boolean;
  serialNo: string;
  showSupport: boolean;
  public selectedCountryCode: CountryCode;
  isDesktop: boolean;
  isDesktopFlag = false;
  isGuestUser: boolean;
  infoMessage: string;
  otpChannel: 'mobile' | 'email' = 'mobile';
  loginMode: 'mobile' | 'email' = 'mobile';
  private atLeastOneContact = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const mobNo = (group.get('mobNo')?.value || '').trim();
    const email = (group.get('email')?.value || '').trim();
    return mobNo || email ? null : { contactRequired: true };
  };
  constructor(
    private navControl: NavController,
    private accountService: AccountService,
    private modalController: ModalController,
    private pushNotificationService: PushNotificationService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private themeService: ThemeService,
    private analyticsService: AnalyticsService,
    private uiService: UiService,
    private authService: AuthenticationService,
    private localStorageService: LocalStorageService,
    private i18Config: I18ConfigService
  ) {
    this.initPageState();
  }

  initPageState() {
    this.showSupport = false;
    this.showOTPInput = false;
    this.showOTPError = false;
    this.selectedCountryCode = this.themeService.getDefaultCountry;
    this.formGrp = new UntypedFormGroup(
      {
        mobNo: new UntypedFormControl('', [Validators.pattern('^[0-9]*$')]),
        email: new UntypedFormControl('', [Validators.email]),
      },
      { validators: this.atLeastOneContact }
    );
    this.formGrp2 = new UntypedFormGroup({
      otp: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(4),
      ]),
    });
    this.appName = this.themeService.getAppName;
    if (
      this.themeService.getClientName === 'thomascook_fc' ||
      this.themeService.getClientName === 'thomascook_lt'
    ) {
      this.appLogo = this.themeService.getAppLogo?.blackLogo;
    } else {
      this.appLogo = this.themeService.getAppLogo?.whiteLogo;
    }
  }

  sendOtpHandler() {
    if (this.loginMode === 'email') {
      this.sendEmailOTP();
    } else {
      this.sendOTP();
    }
  }

  onLoginModeChange(event: any) {
    this.loginMode = event?.detail?.value || this.loginMode;
    if (this.loginMode === 'mobile') {
      this.formGrp.get('email').reset('');
    } else {
      this.formGrp.get('mobNo').reset('');
    }
    this.showOTPInput = false;
    this.msgId = '';
    this.otpId = undefined;
    this.otpChannel = this.loginMode;
  }

  ngOnInit() {
    try {
      this.isGuestUser = this.authService.checkIfGuestUser();
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      this.isGuestUser = true; // Default to guest user on error
    }
    this.isDesktop = window.innerWidth > 520;
    this.route.queryParamMap.subscribe((params) => {
      if (!this.isDesktop) {
        this.isDesktop = true;
      }
    });
    this.appName = this.themeService.getAppName;
  }

  async verifyOTP() {
    this.showOTPError = false;
    const userType =
      this.localStorageService.getItem(localStorageKeys.USER_TYPE) || '';

    const loaderInstance = await this.uiService.presentGlobalLoading(
      this.translate.instant('login.verifying_OTP')
    );

    const isEmailLogin = this.otpChannel === 'email';
    const mobnum = isEmailLogin ? null : this.formGrp.value.mobNo;
    const callingcode = isEmailLogin ? null : this.selectedCountryCode.dialCode;
    const otpEmail = isEmailLogin ? this.formGrp.value.email : null;

    const verifyOtpCall = () => {
      this.accountService
        .verifyOtp(
          mobnum,
          callingcode,
          this.msgId,
          this.otpId,
          this.formGrp2.value.otp,
          userType,
          otpEmail
        )
        .pipe(
          finalize(() => {
            loaderInstance.dismiss();
          })
        )
        .subscribe({
          next: (response) => {
            if (response.status !== 0) {
              this.uiService.presentToast(response.message || response.reason);
              return;
            }
            if (response.token) {
              this.accountService.token = response.token;
              if (!isEmailLogin) {
                this.accountService.phone = this.formGrp.value.mobNo.toString();
              }
              this.pushNotificationService.initPush();
              if (response?.tierInfo?.currency) {
                this.i18Config.setCurrency(response.tierInfo.currency);
              } else {
                this.i18Config.setCurrency(this.themeService.getDefaultCurrency);
              }

              this.localStorageService.removeItem(localStorageKeys.GUEST_TOKEN);

              if (response?.user_info?.user_type) {
                this.localStorageService.setItem(
                  localStorageKeys.USER_TYPE,
                  response?.user_info?.user_type.toString()
                );
              }
              if (response?.user_info) {
                const userData = {
                  name: response.user_info.name,
                  email: response.user_info.email,
                  profileid: response.user_info.profileid,
                  userId: response.user_info.user_id,
                };
                this.localStorageService.setItem(
                  localStorageKeys.USER_INFO,
                  JSON.stringify(userData)
                );
              }
              if (
                response?.user_info?.email === '' ||
                response?.user_info?.dob === ''
              ) {
                this.dismiss(true);
                this.accountService.isMember = false;
              } else {
                let devicePlatform = 'web';
                if (Capacitor.isNativePlatform()) {
                  devicePlatform = Capacitor.getPlatform(); // 'ios' or 'android'
                }
                const loginMethod = isEmailLogin ? 'email' : 'phone';
                if (this.accountService.registered) {
                  if (
                    this.accountService.registered !==
                    response?.user_info?.profileid
                  ) {
                    this.analyticsService.trackLogin(
                      response?.user_info?.profileid,
                      loginMethod,
                      {
                        ...response?.user_info,
                        device: devicePlatform,
                        guest_user_id: this.accountService.registered,
                      }
                    );
                  }
                } else {
                  this.analyticsService.trackLogin(
                    response?.user_info?.profileid,
                    loginMethod,
                    { ...response?.user_info, device: devicePlatform }
                  );
                }
                this.accountService.registered = response?.user_info?.profileid;
                this.accountService.isMember = true;
                this.dismiss(true);
              }
            } else {
              this.showOTPError = true;
              this.showSupport = true;
            }
          },
          error: (e) => {
            this.uiService.presentToast(
              this.translate.instant(
                'login.we_are_unable_to_verify_OTP_at_the_moment_please_try_later'
              ),
              'bottom',
              4000
            );
            this.analyticsService.trackUnified('login_error', {
              flow: 'auth',
              outcome: 'error',
              legacy_event_name: 'Login Error',
              Component: 'mobile-login-modal',
              error: this.translate.instant(
                'login.we_are_unable_to_verify_OTP_at_the_moment_please_try_later'
              ),
            });
            this.showSupport = true;
          },
        });
    };

    this.verifyLogin();

    // if (this.localStorageService.getItem('guestTk') !== null) {
    //   this.accountService
    //     .verifyLogin(
    //       this.formGrp.value.mobNo,
    //       this.localStorageService.getItem('guestTk'),
    //       this.selectedCountryCode.dialCode
    //     )
    //     .subscribe({
    //       next: (response) => {
    //         if (response.token) {
    //           this.accountService.token = response.token;
    //           this.accountService.phone = this.formGrp.value.mobNo.toString();
    //           this.localStorageService.removeItem('guestTk');
    //         }
    //         verifyOtpCall();
    //       },
    //       error: (e) => {
    //         this.analyticsService.trackEvent('Login Error', {
    //           error: e,
    //           Component: 'mobile-login-modal',
    //         });
    //         loaderInstance.dismiss();
    //       },
    //     });
    // } else {
    // }
    verifyOtpCall();
  }

  async sendOTP(resend = 0) {
    this.otpChannel = 'mobile';
    this.analyticsService.trackUnified(
      resend ? 'otp_resend_requested' : 'otp_send_requested',
      {
        flow: 'auth',
        step: 'otp_send',
        step_index: 1,
        outcome: 'success',
        legacy_event_name: resend ? 'Resend OTP' : 'Send OTP',
        component: 'mobile-login-modal',
      }
    );
    // this.verifyLogin();
    this.formGrp2.setValue({ otp: '' });
    const loaderInstance = await this.uiService.presentGlobalLoading(
      this.translate.instant('login.sending_OTP')
    );
    this.accountService
      .sendOtp(this.formGrp.value.mobNo, this.selectedCountryCode.dialCode)
      .pipe(
        finalize(() => {
          loaderInstance.dismiss();
        })
      )
      .subscribe({
        next: async (response) => {
          if (response.status !== 0) {
            if (response.status === -1) {
              this.presentModal(
                // eslint-disable-next-line max-len, @typescript-eslint/quotes
                response?.reason ||
                  this.translate.instant(
                    'login.we_re_sorry_but_it_appears_that_the_account_you_are_trying'
                  )
              );
              this.analyticsService.trackUnified('login_error', {
                flow: 'auth',
                outcome: 'error',
                legacy_event_name: 'Login Error',
                component: 'mobile-login-modal',
                error: this.translate.instant(
                  'login.we_re_sorry_but_it_appears_that_the_account_you_are_trying'
                ),
              });
              return;
            } else {
              this.uiService.presentToast(response.message);
              return;
            }
          }
          this.msgId = response?.msgId;
          this.otpId = response?.otpId;
          this.showOTPInput = true;
          this.showSupport = false;
        },
        error: (e) => {
          this.uiService.presentToast(
            this.translate.instant(
              'login.we_are_unable_to_send_OTP_at_the_moment_please_try_later'
            ),
            'bottom',
            4000
          );
          this.analyticsService.trackUnified('otp_resend_failed', {
            flow: 'auth',
            outcome: 'error',
            legacy_event_name: 'Resend OTP',
            component: 'mobile-login-modal',
            error: this.translate.instant(
              'login.we_are_unable_to_send_OTP_at_the_moment_please_try_later'
            ),
          });
          this.showSupport = true;
        },
      });
  }

  async sendEmailOTP() {
    this.otpChannel = 'email';
    this.formGrp2.setValue({ otp: '' });
    const loaderInstance = await this.uiService.presentGlobalLoading(
      this.translate.instant('login.sending_OTP')
    );
    this.accountService
      .sendEmailOtp(this.formGrp.value.email)
      .pipe(
        finalize(() => {
          loaderInstance.dismiss();
        })
      )
      .subscribe({
        next: async (response) => {
          const msgId = response?.msgId;
          const otpId = response?.otpId;
          if (
            response.email_response?.statusCode === 200 &&
            otpId != null &&
            msgId != null &&
            String(msgId).trim() !== ''
          ) {
            this.msgId = String(msgId).trim();
            this.otpId = otpId;
            this.showOTPInput = true;
            this.showSupport = false;
          } else {
            this.uiService.presentToast(response.message);
            return;
          }
        },
        error: (e) => {
          this.uiService.presentToast(
            this.translate.instant(
              'login.we_are_unable_to_send_OTP_at_the_moment_please_try_later'
            ),
            'bottom',
            4000
          );
          this.analyticsService.trackUnified('login_error', {
            flow: 'auth',
            outcome: 'error',
            legacy_event_name: 'Login Error',
            component: 'mobile-login-modal',
            error: this.translate.instant(
              'login.we_are_unable_to_send_OTP_at_the_moment_please_try_later'
            ),
          });
          this.showSupport = true;
        },
      });
  }

  async presentModal(msg: string) {
    const modal = await this.modalController.create({
      component: GenericModalComponent,
      componentProps: {
        icon: 'TX_WARNING_ICON',
        heading: '',
        message: msg,
        returnFailure: {},
        returnSuccess: { deActivate: 1 },
      },
      showBackdrop: true,
      cssClass: 'generic-modal',
    });

    // this.pageStateService.setReloadAll();
    return await modal.present();
  }

  async selectCountryCode() {
    const modal = await this.modalController.create({
      component: CountryCodeModalComponent,
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    this.selectedCountryCode = data || this.selectedCountryCode;
  }

  loginPage() {
    this.isDesktop = false;
    this.isDesktopFlag = true;
  }

  dismiss(isSuccess: boolean) {
    this.modalController.dismiss({ success: isSuccess });
  }

  verifyLogin() {
    if (this.isGuestUser) {
      this.authService.transferGuestToken();
    } else {
    }
  }

  async openTermsAndConditions() {
    this.analyticsService.trackUnified('terms_opened', {
      flow: 'auth',
      outcome: 'success',
      legacy_event_name: 'Open Terms and Conditions',
      component: 'mobile-login-modal',
    });
    const modal = await this.modalController.create({
      component: TermsConditionsModalComponent,
      showBackdrop: true,
      cssClass: 'terms-conditions-modal',
      componentProps: { useAccountContent: true },
    });
    return await modal.present();
  }

  async openPrivacyPolicy() {
    this.analyticsService.trackUnified('privacy_policy_opened', {
      flow: 'auth',
      outcome: 'success',
      legacy_event_name: 'Open Privacy Policy',
      component: 'mobile-login-modal',
    });
    const modal = await this.modalController.create({
      component: PrivacyPolicyModalComponent,
      showBackdrop: true,
      cssClass: 'privacy-policy-modal',
      componentProps: { useAccountContent: true },
    });
    return await modal.present();
  }

  async onInputFocus(event: any) {
    const modal = await this.modalController.getTop();
    if (modal) {
      modal.setCurrentBreakpoint(1);
    }
  }
}
